import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import PublicActorProfile from "@/pages/PublicActorProfile";
import ScheduleAppointment from "@/pages/ScheduleAppointment";
import AppointmentDetail from "@/pages/AppointmentDetail";
import { Toaster } from "@/components/ui/sonner";
import axiosInstance from "@/config/axios";
import { toast } from "sonner";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Decode JWT to check expiration (without making API call)
        // JWT format: header.payload.signature
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000; // Convert to milliseconds
        const now = Date.now();
        
        // Check if token is expired
        if (now >= exp) {
          // Token expired - clear and logout
          localStorage.removeItem("token");
          localStorage.removeItem("refresh_token");
          localStorage.removeItem("user");
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }
        
        // Token is still valid
        setIsAuthenticated(true);
        setLoading(false);
        
        // Optional: Proactively refresh token if it's close to expiring (within 1 hour)
        const oneHour = 60 * 60 * 1000;
        if (exp - now < oneHour) {
          // Token expires soon, refresh it in background
          axiosInstance.post("/api/auth/refresh")
            .then(response => {
              const { access_token, user } = response.data;
              localStorage.setItem("token", access_token);
              if (user) {
                localStorage.setItem("user", JSON.stringify(user));
              }
            })
            .catch(error => {
              // Refresh failed, but don't logout yet - let normal flow handle it
              console.warn("Background token refresh failed:", error);
            });
        }
      } catch (error) {
        // Invalid token format or decode error
        console.error("Token verification error:", error);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setIsAuthenticated(false);
        setLoading(false);
      }
    };

    verifyToken();

    // Listen for auth-expired event from axios interceptor
    const handleAuthExpired = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setIsAuthenticated(false);
      // Force redirect using replace (doesn't add to history)
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && !currentPath.startsWith('/ai-agent/')) {
        window.location.replace('/login');
      }
    };

    window.addEventListener('auth-expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-slate-50"></div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Route - No Authentication Required */}
          <Route path="/ai-agent/:agentName" element={<PublicActorProfile />} />
          <Route path="/schedule-appointment" element={<ScheduleAppointment />} />
          <Route path="/appointment-detail" element={<AppointmentDetail />} />
          
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Login setIsAuthenticated={setIsAuthenticated} />
              )
            }
          />
          <Route
            path="/*"
            element={
              isAuthenticated ? (
                <Dashboard setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;